import React, { useRef, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

// Used by parent for map overlay / FAB offset calculations
// = top-padding(10) + card-height(160) + bottom-safe-area-approx(44)
const CAROUSEL_HEIGHT = 220;
const DAY_COLORS = ['#EA4335', '#FBBC04', '#34A853', '#4285F4', '#A142F4', '#FF6D00'];
const BLOCK_ICONS = { morning: '🌅', afternoon: '☀️', evening: '🌙' };
const BLOCK_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

/* ─── Wrapper sits above map bottom, height driven by content ─── */
const CarouselWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  z-index: 10;
  pointer-events: none;
`;

const ScrollTrack = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  /* left: card indent | right: small so next card peeks | bottom: safe area */
  padding: 10px 8px max(22px, env(safe-area-inset-bottom)) 16px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  pointer-events: auto;
  touch-action: pan-x;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
`;

/* ─── Card — two visual modes: photo bg vs plain white ─── */
const Card = styled.div`
  flex-shrink: 0;
  /* Fill most of viewport width; the remainder peeks the next card */
  width: calc(100vw - 72px);
  max-width: 340px;
  height: 160px;
  border-radius: 18px;
  scroll-snap-align: start;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
  /* pan-x lets horizontal scroll pass through; never use pan-y on carousel cards */
  touch-action: pan-x;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.22), 0 1px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.15s ease, outline-color 0.15s;

  &:active {
    transform: scale(0.97);
  }

  /* border highlight when selected */
  outline: ${p => p.$selected ? `2.5px solid ${p.$color || '#4285F4'}` : '2.5px solid transparent'};
  outline-offset: -2.5px;

  /* plain white bg when no photo */
  ${p => !p.$hasPhoto && css`
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  `}
`;

/* Photo fills the whole card */
const CardPhoto = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

/* Dark gradient overlay so text is readable on photos */
const PhotoOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.80) 0%,
    rgba(0, 0, 0, 0.30) 55%,
    transparent 100%
  );
`;

/* Text content inside the card */
const CardContent = styled.div`
  position: absolute;
  inset: 0;
  padding: 12px 14px 13px 14px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const DayBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 2px 9px;
  background: ${p => p.$hasPhoto ? 'rgba(255,255,255,0.18)' : (p.$color + '22')};
  color: ${p => p.$hasPhoto ? 'rgba(255,255,255,0.92)' : p.$color};
  border-radius: 10px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  backdrop-filter: ${p => p.$hasPhoto ? 'blur(4px)' : 'none'};
`;

const ActivityName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.$hasPhoto ? '#f9fafb' : '#111827'};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: ${p => p.$hasPhoto ? '0 1px 3px rgba(0,0,0,0.5)' : 'none'};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${p => p.$hasPhoto ? 'rgba(255,255,255,0.75)' : '#6b7280'};
`;

const MetaDot = styled.span`opacity: 0.5; font-size: 10px;`;

/* ─── Gather all activities from all days into flat list ─── */
export const gatherActivities = (dailyItinerary) => {
  const items = [];
  if (!dailyItinerary) return items;

  dailyItinerary.forEach((day, dayIdx) => {
    const dayNum = day.day || dayIdx + 1;
    const color = DAY_COLORS[dayIdx % DAY_COLORS.length];

    ['morning', 'afternoon', 'evening'].forEach((block) => {
      const acts = day[block];
      if (!acts || !Array.isArray(acts)) return;
      acts.forEach((a, actIdx) => {
        if (!a || typeof a !== 'object') return;
        const name = a.activity || a.name;
        if (!name) return;
        items.push({
          id: `day${dayNum}-${block}-${actIdx}`,
          name,
          description: a.description || '',
          time: a.time || '',
          block,
          duration: a.duration,
          dayNum,
          dayIdx,
          color,
          latitude: a.latitude,
          longitude: a.longitude,
          place: a.place,
        });
      });
    });
  });

  return items;
};

/* photoCache: { [activityId]: photoUrl } passed from parent */
const ActivityCardCarousel = ({ dailyItinerary, selectedCardId, onCardTap, photoCache = {} }) => {
  const activities = useMemo(() => gatherActivities(dailyItinerary), [dailyItinerary]);
  const scrollRef = useRef(null);

  // Auto-scroll to selected card
  useEffect(() => {
    if (!selectedCardId || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-id="${selectedCardId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedCardId]);

  if (!activities.length) return null;

  return (
    <CarouselWrapper>
      <ScrollTrack ref={scrollRef}>
        {activities.map((act) => {
          const thumbUrl = photoCache[act.id] || null;
          const hasPhoto = !!thumbUrl;
          const isSelected = selectedCardId === act.id;

          return (
            <Card
              key={act.id}
              data-id={act.id}
              $selected={isSelected}
              $color={act.color}
              $hasPhoto={hasPhoto}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onCardTap(act)}
            >
              {hasPhoto && <CardPhoto src={thumbUrl} alt={act.name} loading="lazy" />}
              {hasPhoto && <PhotoOverlay />}

              <CardContent>
                <DayBadge $color={act.color} $hasPhoto={hasPhoto}>DAY {act.dayNum}</DayBadge>
                <ActivityName $hasPhoto={hasPhoto} title={act.name}>{act.name}</ActivityName>
                <Meta $hasPhoto={hasPhoto}>
                  <span>{BLOCK_ICONS[act.block] || '📍'}</span>
                  <span>{BLOCK_LABELS[act.block] || act.time}</span>
                  {act.duration && (
                    <><MetaDot>·</MetaDot><span>{act.duration}</span></>
                  )}
                </Meta>
              </CardContent>
            </Card>
          );
        })}
      </ScrollTrack>
    </CarouselWrapper>
  );
};

export { CAROUSEL_HEIGHT, DAY_COLORS };
export default ActivityCardCarousel;
